<template>
  <div class="banner" :style="{ aspectRatio }">
    <!-- Loading -->
    <div v-if="loading" class="banner-skeleton" aria-busy="true"></div>

    <!-- Slides -->
    <Carousel
      v-else-if="slides.length"
      v-bind="settings"
      class="banner-carousel"
      :aria-label="ariaLabel"
    >
      <Slide v-for="(slide, index) in slides" :key="slide.id || index">
        <!-- External link -->
        <a
          v-if="slide.ExternalLink"
          :href="slide.ExternalLink"
          target="_blank"
          rel="noopener noreferrer"
          class="banner-link"
        >
          <img :src="imgSrc(slide)" :alt="altText(slide)" :loading="index ? 'lazy' : 'eager'" />
        </a>

        <!-- Announcement page -->
        <router-link v-else :to="`/announcement/${slide.id}`" class="banner-link">
          <img :src="imgSrc(slide)" :alt="altText(slide)" :loading="index ? 'lazy' : 'eager'" />
        </router-link>
      </Slide>

      <template #addons>
        <Navigation v-if="slides.length > 1" />
        <Pagination v-if="slides.length > 1" />
      </template>
    </Carousel>

    <!-- Nothing to show -->
    <div v-else class="banner-empty">
      <span class="material-icons">campaign</span>
    </div>
  </div>
</template>

<script>
import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide, Pagination, Navigation } from "vue3-carousel";

const NEWS_HOST = "https://news.spacesaver.com";

export default {
  name: "NewCarousel",
  components: { Carousel, Slide, Pagination, Navigation },
  props: {
    slides: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    // Match your banner artwork; 3:1 ≈ the old 260px-tall banner in an 8-column box
    aspectRatio: { type: String, default: "3 / 1" },
    ariaLabel: { type: String, default: "Announcements" },
  },
  data() {
    return {
      // vue3-carousel reads these as individual props (v-bind), not a "settings" object
      settings: {
        itemsToShow: 1,
        wrapAround: true,
        autoplay: 5000,           // ms between slides
        pauseAutoplayOnHover: true,
        transition: 500,
      },
    };
  },
  methods: {
    imgSrc(slide) {
      const url = slide?.SliderImage?.url || "";
      return /^https?:\/\//.test(url) ? url : NEWS_HOST + url;
    },
    altText(slide) {
      return slide.alternativeText || slide.SliderImage?.alternativeText || slide.title || "Announcement";
    },
  },
};
</script>

<style scoped>
.banner {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #eceff1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.banner-carousel,
.banner-carousel :deep(.carousel__viewport),
.banner-carousel :deep(.carousel__track),
.banner-carousel :deep(.carousel__slide) {
  height: 100%;
}
.banner-link {
  display: block;
  width: 100%;
  height: 100%;
}
.banner-link img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.banner-link:focus-visible {
  outline: 3px solid #429da8;
  outline-offset: -3px;
}

/* Arrows: round, white, only fully visible on hover */
.banner-carousel :deep(.carousel__prev),
.banner-carousel :deep(.carousel__next) {
  width: 38px;
  height: 38px;
  margin: 0 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #1e3040;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  opacity: 0.6;
  transition: opacity 0.2s ease;
}
.banner:hover :deep(.carousel__prev),
.banner:hover :deep(.carousel__next),
.banner-carousel :deep(.carousel__prev:focus-visible),
.banner-carousel :deep(.carousel__next:focus-visible) {
  opacity: 1;
}

/* Dots */
.banner-carousel :deep(.carousel__pagination) {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
}
.banner-carousel :deep(.carousel__pagination-button::after) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}
.banner-carousel :deep(.carousel__pagination-button--active::after) {
  background: #fff;
  width: 20px;
  border-radius: 4px;
}

/* States */
.banner-skeleton {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #eceff1 25%, #f6f7f8 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: banner-shimmer 1.2s infinite;
}
@keyframes banner-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
.banner-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c4c9ce;
}
.banner-empty .material-icons {
  font-size: 48px;
}
</style>
